import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CHANGEABLE_FIELDS, EVENT_TYPES, FLIGHT_LEGS } from './api';
import type { ChangeableField, EventType, FlightLeg } from './api';

/**
 * The monitor's state, held in the URL and nowhere else — same rule as every other
 * screen here. One hook for all three views, because they share the source filter:
 * "show me this source's errors" is a link from the sources view into the events
 * view, and it only works if both read the same parameter.
 *
 * `type`, `leg` and `field` are validated against their enums before a request is
 * built: those lists are the API's own contract and cannot drift. `source` is NOT,
 * because which sources exist is the server's to say and a mirrored list here fell
 * behind it — see the field. An unknown source comes back as a 400 that
 * `useMonitor` reports as a rejected filter.
 */

export const MONITOR_VIEWS = ['sources', 'events', 'messages'] as const;
export type MonitorView = (typeof MONITOR_VIEWS)[number];

const KEYS = {
  view: 'view',
  source: 'source',
  type: 'type',
  leg: 'leg',
  /** A changed column — `changedField` on the wire. */
  field: 'field',
  flight: 'flight',
  /** An online-table row id — the "whole history of this row" filter. */
  scheduleId: 'row',
  /** How far back to look. Absent means the server's own last-48-hours window. */
  days: 'days',
  page: 'page',
  rows: 'rows',
  /** The source whose settings panel is open. */
  panel: 'id',
} as const;

/**
 * The period, as a number of days back rather than two timestamps.
 *
 * The server windows an unfiltered feed to 48 hours and takes `createdFrom` to go
 * further. Storing a count instead of an instant means a shared link still means
 * "the last week" tomorrow — the same reason the flight board stores a named range
 * rather than the dates it resolved to.
 */
export const DAY_RANGES = [2, 7, 30, 90] as const;
export type DayRange = (typeof DAY_RANGES)[number];

const isDayRange = (value: number): value is DayRange =>
  (DAY_RANGES as readonly number[]).includes(value);

/** `yyyy-MM-dd'T'HH:mm:ss` from the LOCAL clock — the format the API documents. */
export const apiTimestamp = (date: Date): string => {
  const pad = (part: number) => String(part).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
};

export const ROWS_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_ROWS = 50;

export interface MonitorParams {
  view: MonitorView;
  /**
   * The source to filter by, taken from the URL as written.
   *
   * NOT checked against a list of known codes. It used to be, against a hardcoded
   * one, and that is what silently dropped the filter for a source the list had
   * fallen behind on. The dropdown offers only codes the SERVER named, and a value
   * the server does not know comes back as a 400 that `useMonitor` already reports
   * as a rejected filter — which is a better answer than pretending no filter was
   * asked for.
   */
  source: string;
  type: EventType | '';
  leg: FlightLeg | '';
  field: ChangeableField | '';
  flight: string;
  scheduleId: number | null;
  /** `null` = leave the period to the server, which means the last 48 hours. */
  days: DayRange | null;
  page: number;
  rows: number;
  /**
   * The source whose settings panel is open.
   *
   * Taken as written, like `source` above. It used to be checked against a list of
   * codes hardcoded in this feature, and that is what stopped `NQZ_AMS_FTP`
   * opening: the code was missing from the list, so clicking the row wrote
   * `?id=NQZ_AMS_FTP` and this reader handed back `null` on the very next render —
   * silently, because a closed panel looks exactly like a click that never landed.
   *
   * Nothing is requested from it either: the view matches it against the sources it
   * already fetched, so an unknown one finds no row and the panel stays shut, which
   * is the right answer for a stale link.
   */
  panel: string | null;
}

const isOneOf = <T extends string>(options: readonly T[], value: string | null): value is T =>
  value !== null && (options as readonly string[]).includes(value);

export const useMonitorParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<MonitorParams>(() => {
    const view = searchParams.get(KEYS.view);
    const source = searchParams.get(KEYS.source);
    const type = searchParams.get(KEYS.type);
    const leg = searchParams.get(KEYS.leg);
    const field = searchParams.get(KEYS.field);
    const panel = searchParams.get(KEYS.panel);
    const scheduleId = Number(searchParams.get(KEYS.scheduleId));
    const days = Number(searchParams.get(KEYS.days));
    const page = Number(searchParams.get(KEYS.page));
    const rows = Number(searchParams.get(KEYS.rows));

    return {
      view: isOneOf(MONITOR_VIEWS, view) ? view : 'sources',
      source: source?.trim() ?? '',
      type: isOneOf(EVENT_TYPES, type) ? type : '',
      leg: isOneOf(FLIGHT_LEGS, leg) ? leg : '',
      field: isOneOf(CHANGEABLE_FIELDS, field) ? field : '',
      flight: searchParams.get(KEYS.flight)?.trim() ?? '',
      scheduleId: Number.isInteger(scheduleId) && scheduleId > 0 ? scheduleId : null,
      days: isDayRange(days) ? days : null,
      page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1,
      rows: Number.isFinite(rows) && rows >= 1 ? Math.floor(rows) : DEFAULT_ROWS,
      // Taken as written — see the field's note. The `source` FILTER above is
      // validated because the server 400s on an unknown one; this is not sent
      // anywhere, so the fetched list is what decides whether it names anything.
      panel: panel?.trim() || null,
    };
  }, [searchParams]);

  /**
   * Applies a change — filters, view, page and the open panel alike.
   *
   * **One function, deliberately.** `setSearchParams` builds its next value from the
   * params of the CURRENT RENDER, not from a pending update, so two calls in one
   * handler do not compose: the second navigation is computed from the same starting
   * point as the first and overwrites it. Splitting "change the filters" and "close
   * the panel" into two functions made a handler that did both silently lose one of
   * them. Anything that changes together has to change in a single call.
   *
   * Anything but the page itself resets to page 1 — a narrower filter otherwise
   * leaves the reader on a page that no longer exists, and each view pages its own
   * list, so switching view resets it too. Opening or closing the panel is not a
   * filter change and leaves the page alone.
   */
  const update = useCallback(
    (changes: Partial<MonitorParams>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          for (const [key, value] of Object.entries(changes)) {
            const param = KEYS[key as keyof typeof KEYS];

            if (value === '' || value === undefined || value === null) next.delete(param);
            else next.set(param, String(value));
          }

          const onlyPanel = Object.keys(changes).every((key) => key === 'panel');
          if (!('page' in changes) && !onlyPanel) next.delete(KEYS.page);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Opening or closing the settings panel — `update` under a name that reads. */
  const select = useCallback((panel: string | null) => update({ panel }), [update]);

  const hasFilters =
    params.source !== '' ||
    params.type !== '' ||
    params.leg !== '' ||
    params.field !== '' ||
    params.flight !== '' ||
    params.scheduleId !== null ||
    params.days !== null;

  const reset = useCallback(
    () =>
      update({
        source: '',
        type: '',
        leg: '',
        field: '',
        flight: '',
        scheduleId: null,
        days: null,
      }),
    [update],
  );

  return { params, update, select, reset, hasFilters };
};
